// Auth flows: setup password (invitation), forgot & reset
import { useState, useEffect } from "react";
import axios from "axios";
import { auth } from "./api";
import { C, Btn, Input, Alert } from "./shared";

const AuthShell = ({ children }) => (
  <div style={{ position: "fixed", inset: 0, background: C.navy, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
    <div style={{ background: "rgba(255,255,255,.04)", border: `1px solid rgba(200,144,44,.2)`, borderRadius: 20, padding: 40, width: 440, maxWidth: "100%" }}>
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 34, color: "#fff", letterSpacing: ".04em" }}>
          <span>AU</span><span style={{ color: C.gold }}>PRÉA</span>
        </div>
        <div style={{ fontSize: 11, color: C.gold, fontStyle: "italic", marginTop: 4 }}>vous accompagne</div>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, ...props }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,.55)", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 6 }}>{label}</label>
    <input {...props} style={{ width: "100%", background: "rgba(255,255,255,.06)", border: `1px solid rgba(255,255,255,.14)`, borderRadius: 8, padding: "11px 14px", color: "#fff", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
  </div>
);

const SubmitBtn = ({ children, disabled, onClick, ...rest }) => (
  <button {...rest} disabled={disabled} onClick={onClick} style={{ width: "100%", background: C.gold, color: C.navy, border: "none", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 700, cursor: disabled ? "wait" : "pointer", fontFamily: "inherit", opacity: disabled ? .7 : 1 }}>
    {children}
  </button>
);

const validPwd = (p) => p.length >= 8 && /\d/.test(p);

// ── Setup Password (via invitation) ──────────────────────────────────────────
const NouveauLien = ({ token }) => {
  const [msg, setMsg] = useState(null);
  const go = async () => {
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/public/integration/nouveau-lien`, { token });
      setMsg({ ok: true, t: `Un nouveau lien (valable 48 h) a été envoyé à ${r.data.email}.` });
    } catch (e) { setMsg({ ok: false, t: e?.response?.data?.detail || "Erreur" }); }
  };
  return msg ? <div style={{ color: msg.ok ? "#86efac" : "#fca5a5", fontSize: 12, textAlign: "center", marginBottom: 14 }} data-testid={msg.ok ? "nouveau-lien-msg" : "nouveau-lien-erreur"}>{msg.t}</div>
    : <div style={{ marginBottom: 12 }}><SubmitBtn onClick={go} data-testid="btn-nouveau-lien">Recevoir un nouveau lien</SubmitBtn></div>;
};

export const SetupPasswordPage = ({ token, onSuccess }) => {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [expire, setExpire] = useState(false);
  const [pwd, setPwd] = useState(""); const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    auth.checkInvitation(token)
      .then(setInfo)
      .catch(e => { setExpire(e?.response?.status === 410); setErr(e?.response?.data?.detail || "Lien invalide"); });
  }, [token]);
  const submit = async () => {
    setErr("");
    if (!validPwd(pwd)) return setErr("8 caractères min. dont un chiffre");
    if (pwd !== pwd2) return setErr("Les mots de passe ne correspondent pas");
    setLoading(true);
    try {
      await auth.completeInvitation(token, pwd);
      onSuccess();
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <AuthShell>
      <div style={{ color: "#fff", textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontFamily: "'Playfair Display', serif", marginBottom: 6 }}>Bienvenue dans le réseau AUPRÉA</div>
        {info && <div style={{ fontSize: 12, color: "rgba(255,255,255,.5)" }}>Définissez votre mot de passe · {info.email}</div>}
      </div>
      {err && <div style={{ background: "rgba(153,27,27,.2)", color: "#fca5a5", border: "1px solid rgba(153,27,27,.4)", borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 14 }}>{err}</div>}
      {expire && <NouveauLien token={token} />}
      {info && <>
        <Field label="Nouveau mot de passe" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="8 caractères min. dont un chiffre" />
        <Field label="Confirmez le mot de passe" type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()} />
        <SubmitBtn disabled={loading} onClick={submit}>{loading ? "…" : "Activer mon compte"}</SubmitBtn>
      </>}
      <div style={{ fontSize: 10, color: "rgba(255,255,255,.35)", textAlign: "center", marginTop: 16 }}>
        Le lien est valable 48 h · usage unique
      </div>
    </AuthShell>
  );
};

// ── Reset Password (via forgot link) ─────────────────────────────────────────
export const ResetPasswordPage = ({ token, onSuccess }) => {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [pwd, setPwd] = useState(""); const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    auth.checkReset(token)
      .then(setInfo)
      .catch(e => setErr(e?.response?.data?.detail || "Lien invalide"));
  }, [token]);
  const submit = async () => {
    setErr("");
    if (!validPwd(pwd)) return setErr("8 caractères min. dont un chiffre");
    if (pwd !== pwd2) return setErr("Les mots de passe ne correspondent pas");
    setLoading(true);
    try {
      await auth.reset(token, pwd);
      onSuccess();
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <AuthShell>
      <div style={{ color: "#fff", textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontFamily: "'Playfair Display', serif", marginBottom: 6 }}>Réinitialiser mon mot de passe</div>
        {info && <div style={{ fontSize: 12, color: "rgba(255,255,255,.5)" }}>{info.email}</div>}
      </div>
      {err && <div style={{ background: "rgba(153,27,27,.2)", color: "#fca5a5", border: "1px solid rgba(153,27,27,.4)", borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 14 }}>{err}</div>}
      <Field label="Nouveau mot de passe" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="8 caractères min. dont un chiffre" />
      <Field label="Confirmez le mot de passe" type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()} />
      <SubmitBtn disabled={loading || !info} onClick={submit}>{loading ? "…" : "Valider"}</SubmitBtn>
      <div style={{ fontSize: 10, color: "rgba(255,255,255,.35)", textAlign: "center", marginTop: 16 }}>
        Le lien est valable 30 minutes
      </div>
    </AuthShell>
  );
};

// ── Forgot Password Modal ────────────────────────────────────────────────────
export const ForgotPasswordModal = ({ onClose }) => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try { await auth.forgot(email); setDone(true); }
    catch { setDone(true); } // toujours message générique
    finally { setLoading(false); }
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 16, width: 420, maxWidth: "100%", padding: 32 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: C.navy, marginBottom: 6 }}>Mot de passe oublié ?</div>
        {done ? (
          <>
            <Alert type="info"><span>📧</span><span>Si un compte existe pour cet email, un lien de réinitialisation a été envoyé.</span></Alert>
            <div style={{ fontSize: 12, color: C.mid, marginBottom: 16 }}>Vérifiez également vos spams. Le lien est valable 30 minutes.</div>
            <div style={{ textAlign: "right" }}><Btn variant="gold" onClick={onClose}>Fermer</Btn></div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: C.mid, marginBottom: 16 }}>Entrez votre email, nous vous enverrons un lien pour réinitialiser votre mot de passe.</div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: C.mid, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 5 }}>Email</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="votre@email.com" />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Btn onClick={onClose}>Annuler</Btn>
              <Btn variant="gold" disabled={loading || !email} onClick={submit}>{loading ? "…" : "Envoyer le lien"}</Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Success screen after setup/reset ─────────────────────────────────────────
export const AuthSuccess = ({ title, message, onLogin }) => (
  <AuthShell>
    <div style={{ textAlign: "center", color: "#fff" }}>
      <div style={{ fontSize: 44, marginBottom: 10 }}>✓</div>
      <div style={{ fontSize: 18, fontFamily: "'Playfair Display', serif", marginBottom: 10 }}>{title}</div>
      <div style={{ fontSize: 13, color: "rgba(255,255,255,.6)", marginBottom: 24 }}>{message}</div>
      <SubmitBtn onClick={onLogin}>Se connecter</SubmitBtn>
    </div>
  </AuthShell>
);
